// directive for trimming a broadcast's recording
//
//    <recording-trimmer broadcast-id="10001"
//                       recording-id="20001"
//                       start-segment="vm.startSegment"
//                       stop-segment="vm.stopSegment"
//                       start-seconds="vm.startSeconds"
//                       stop-seconds="vm.stopSeconds"
//                       on-change-bounds="vm.updateBounds" />
//
//    updateBounds(startSeconds, stopSeconds, startSegment, stopSegment) {
//      ...
//    }
import { secondsToFormattedLabel } from '../utils/date';

const THUMBNAIL_TIMEOUT = 1000;

let module = angular.module('app.directives');

module.directive('recordingTrimmer', () => {
  'ngInject';

  return {
    restrict: 'E',
    scope: {
      broadcastId: '=',
      recordingId: '=',
      onChangeBounds: '=',
      startSegment: '=',
      stopSegment: '=',
      startSeconds: '=',
      stopSeconds: '=',
    },
    template: require('./recording-trimmer.html'),
    link: (scope, element, attrs) => {},
  };
});

module.controller(
  'RecordingTrimmerController',
  class RecordingTrimmerController {
    constructor($scope, Recording, $q, $timeout, toaster) {
      'ngInject';

      this.$scope = $scope;
      this.Recording = Recording;
      this.$q = $q;
      this.$timeout = $timeout;
      this.toaster = toaster;

      this.zoom = 1.0;

      // Wait for broadcast and recording to get attached to scope before initializing
      // XXX: work around inability to attach controller directly to directive
      // definition, and instead attaching it via ngController in the template
      const parentScope = $scope.$parent;
      $scope.$watchCollection(
        () => [parentScope.broadcastId, parentScope.recordingId],
        (args) => args[0] && args[1] && this.init(...args)
      );

      // Watch for external changes to the start/stop segments/seconds if the callee so chooses
      // (dangerous)
      $scope.$watchCollection(
        () => [parentScope.startSegment, parentScope.stopSegment],
        (args) => this.setTrimBoundsFromSegments(args[0], args[1])
      );
      $scope.$watchCollection(
        () => [parentScope.startSeconds, parentScope.stopSeconds],
        (args) => this.setTrimBoundsFromSeconds(args[0], args[1])
      );
    }

    init(broadcastId, recordingId) {
      const { Broadcast, Recording, $q, $timeout, $scope } = this;

      this.showPreviewStartTimer = 0;
      this.showPreviewStopTimer = 0;
      this.trimBounds = [0, 0];

      this.sliderOptsTrim = {
        floor: 0,
        ceil: 1,
        //draggableRange: true,
        onlyBindHandles: true,
        keyboardSupport: false,
        translate: (index, id, what) => this.labelFor(index, id, what),
      };

      this.sliderOptsPlayhead = {
        floor: 0,
        ceil: 1,
        hideLimitLabels: true,
        translate: (index, id, what) => this.labelFor(index, id, what),
      };

      // XXX: there appears to be some sort of race condition, where 2 simultaenous requests to the
      // same URL will cause the the resource to only be resolved once (in the container controller,
      // not this directive).  Adding a `cb` (cachebust) querystring param works around this for now.
      this.recording = Recording.get({ id: recordingId, cb: Math.random() });
      var recordingPromise = this.recording.$promise;
      this.segments = Recording.segments({ id: recordingId, cb: Math.random() });
      var segmentsPromise = this.segments.$promise;
      $q.all([segmentsPromise, recordingPromise]).then((data) => {
        this.initSegments(data[0]); //segments must come before recording so stopSegments can be populated
        this.initRecording(data[1]);
        this.sliderOptsTrim.ceil = this.stopSegments.length - 1;
        this.sliderOptsPlayhead.floor = this.trimBounds[0];
        this.sliderOptsPlayhead.ceil = this.trimBounds[1];
        this.playhead = this.trimBounds[0];
        this.recordingDuration = this.stopSegments[this.stopSegments.length - 1].label;
        this.initWatchers();
        $timeout(() => {
          // Need to kick the rzslider in the teeth a bit
          $scope.$broadcast('reCalcViewDimensions');
          $scope.$broadcast('rzSliderForceRender');
        }, 250);
      });

      // XXX: $q.all swallows errors so need to do this individually
      segmentsPromise.catch((e) => {
        console.error(e);
        this.toaster.pop(
          'warning',
          'Unable to load segments. Please refresh the page and try again'
        );
      });
      recordingPromise.catch((e) => {
        console.error(e);
        this.toaster.pop(
          'warning',
          'Unable to load the recording. Please refresh the page and try again'
        );
      });

      this.loadingPromises = [recordingPromise, segmentsPromise];
    }

    labelFor(index, id, what) {
      // Custom angular-slider translate function uses "floor", "ceil", "model", "high" to distinguish
      // which of the 4 labels it's binding to.  We map those to either start segment or stop segment labels.
      if (what == 'floor' || what == 'model') {
        return this.startSegments && this.startSegments[index] && this.startSegments[index].label;
      } else {
        return this.stopSegments && this.stopSegments[index] && this.stopSegments[index].label;
      }
    }

    segmentPreviewURL(segmentIdx) {
      if (!this.previewBaseURL || !this.previewQueryString) {
        return null;
      }
      const vodPaths = ((this.segments || [])[segmentIdx] || {}).vod_paths || {};
      let thumbPath = vodPaths.thumbs;
      if (!thumbPath) {
        // Some (older?) recordings don't have a `thumbs` key. So try to grab _any_ value from the
        // VOD paths and turn it into a jpg. If that doesn't work, just try string formatting it.
        const fname = _.find(vodPaths);
        if (fname && fname.match(/\d{8}\.[^\.]+$/)) {
          thumbPath = fname.replace(/\.[^\.]+$/, '.jpg');
        } else {
          thumbPath = ('00000000' + segmentIdx).slice(-8) + '.jpg'; // :shrug:
        }
      }
      return this.previewBaseURL + thumbPath + '?' + this.previewQueryString;
    }

    startTimerForPreviewStart() {
      let { $timeout } = this;
      this.showPreviewStartTimer = new Date().getTime();
      this.showPreviewStart = true;
      $timeout(() => {
        if (this.showPreviewStartTimer <= new Date().getTime() - THUMBNAIL_TIMEOUT) {
          this.showPreviewStart = false;
        }
      }, THUMBNAIL_TIMEOUT);
    }

    startTimerForPreviewStop() {
      let { $timeout } = this;
      this.showPreviewStopTimer = new Date().getTime();
      this.showPreviewStop = true;
      $timeout(() => {
        if (this.showPreviewStopTimer <= new Date().getTime() - THUMBNAIL_TIMEOUT) {
          this.showPreviewStop = false;
        }
      }, THUMBNAIL_TIMEOUT);
    }

    setTrimBoundsFromSeconds(startSeconds, stopSeconds) {
      if (this.stopSegments === undefined) {
        this.startSeconds = startSeconds;
        this.stopSeconds = stopSeconds;
        return;
      }

      startSeconds = this.numberOr(startSeconds, 0);
      stopSeconds = this.numberOr(
        stopSeconds,
        this.stopSegments[this.stopSegments.length - 1].seconds
      );
      let startIndex = _.findIndex(this.stopSegments, (segment) => {
        return startSeconds < segment.seconds;
      });
      let stopIndex = _.findIndex(this.stopSegments, (segment) => {
        return stopSeconds <= segment.seconds;
      });
      this.setTrimBounds(startIndex, stopIndex);
    }

    setTrimBoundsFromSegments(startSegment, stopSegment) {
      if (this.startSegments === undefined) {
        this.startSegment = startSegment;
        this.stopSegment = stopSegment;
        return;
      }

      startSegment = this.numberOr(startSegment, 0);
      stopSegment = this.numberOr(stopSegment, this.segments.length - 1);
      let startIndex = _.findIndex(this.startSegments, (segment) => {
        return segment.segmentIndex === startSegment;
      });
      let stopIndex = this.startSegments.length - 1;
      _.each(this.stopSegments, (segment) => {
        // There could be multiple segments that have the same segmentIndex, so iterate through
        // all to get the last one.
        if (segment.segmentIndex === stopSegment) {
          stopIndex = segment.segmentIndex;
        } else if (segment.segmentIndex > stopSegment) {
          return false; // Exit early.
        }
      });
      this.setTrimBounds(startIndex, stopIndex);
    }

    setTrimBounds(startIndex, stopIndex) {
      if (startIndex === -1) {
        startIndex = 0;
      }
      if (stopIndex === -1) {
        stopIndex = this.stopSegments.length - 1;
      }
      this.trimBounds = [startIndex, stopIndex];
      this.playhead = this.trimBounds[0];
      this.playheadControl = {};
    }

    numberOr(number, defaultValue) {
      // NOTE: "" or null should return the defaultValue but "0" should stay 0
      if (!isNaN(parseInt(number))) {
        return number;
      } else {
        return defaultValue;
      }
    }

    initWatchers() {
      let { $scope } = this;

      $scope.$watch(
        () => this.segments && this.previewQueryString && this.previewBaseURL && this.trimBounds[0],
        (v) => {
          v = v || 0;
          const segmentIndex = this.startSegments[v].segmentIndex;
          this.previewStartUrl = this.segmentPreviewURL(segmentIndex) || '/img/recording-start.png';
          this.showPreviewStart = true;
          this.startTimerForPreviewStart();

          this.sliderOptsPlayhead.floor = this.trimBounds[0];
        }
      );

      $scope.$watch(
        () => this.segments && this.previewQueryString && this.previewBaseURL && this.trimBounds[1],
        (v) => {
          v = (v || 0) + 1;
          if (this.startSegments.length == v) {
            this.previewStopUrl = '/img/recording-end.png';
          } else {
            const segmentIndex = this.startSegments[v].segmentIndex;
            this.previewStopUrl = this.segmentPreviewURL(segmentIndex);
          }
          this.showPreviewStop = true;
          this.startTimerForPreviewStop();

          this.sliderOptsPlayhead.ceil = this.trimBounds[1];
        }
      );

      $scope.$watch(
        () => this.segments && this.previewQueryString && this.previewBaseURL && this.playhead,
        (v) => {
          v = v || 0;
          if (v == this.startSegments.length) {
            this.previewUrl = '/img/recording-end.png';
            this.previewCaption = this.stopSegments[v - 1].label;
          } else {
            const segmentIndex = this.startSegments[v].segmentIndex;
            this.previewUrl = this.segmentPreviewURL(segmentIndex);
            this.previewCaption = this.startSegments[v || 0].label;
          }
        }
      );

      $scope.$watchCollection(
        () => this.trimBounds,
        () => this.onTrimBoundsChanged()
      );
    }

    onTrimBoundsChanged() {
      // user dragged the left/right handles (or they changed externally)
      // show them the preview under their handles
      const { $scope, $timeout } = this;

      /*
      * The following logic attempted to smartly keep the playhead where it was
      * when it made sense to do so but had some strange side-effects
    if (this.playhead < this.trimBounds[0]) {
      this.playhead = this.trimBounds[0];
    } else if (this.playhead > (1 + this.trimBounds[1])) {
      this.playhead = (1 + this.trimBounds[1]);
    }
    */
      // Any time you change bounds, reset playhead to start of clip
      this.playhead = this.trimBounds[0];

      if (_.isFunction($scope.onChangeBounds)) {
        $scope.onChangeBounds(
          this.startSegments[this.trimBounds[0]].seconds,
          this.stopSegments[this.trimBounds[1]].seconds,
          this.startSegments[this.trimBounds[0]].segmentIndex,
          this.stopSegments[this.trimBounds[1]].segmentIndex
        );
      }

      $timeout(() => this.positionMasks());
    }

    positionMasks() {
      // position the shaders over the to-be-hidden regions that were clipped

      // position the cut point previews exactly
      let handles = $('.trim .rz-pointer');
      let leftHandle = $(handles.get(0));
      let rightHandle = $(handles.get(1));

      $('.start-point-preview').css({ left: leftHandle.css('left') });
      $('.end-point-preview').css({ left: rightHandle.css('left') });

      $('.start-point-mask').css({ width: leftHandle.css('left') });
      $('.end-point-mask').css({ left: rightHandle.css('left') });
    }

    initRecording(recording) {
      // Initialize to be full length of recording, unless it was set externally already
      if (this.startSegment !== undefined) {
        this.setTrimBoundsFromSegments(this.startSegment, this.stopSegment);
      } else if (this.startSeconds !== undefined) {
        this.setTrimBoundsFromSeconds(this.startSeconds, this.stopSeconds);
      } else if (this.recording.start_segment !== '') {
        this.setTrimBoundsFromSegments(recording.start_segment, recording.stop_segment);
      } else if (this.recording.start_seconds !== '') {
        this.setTrimBoundsFromSeconds(recording.start_seconds, recording.stop_seconds);
      } else {
        this.setTrimBounds(0, this.startSegments.length - 1);
      }

      // Pick apart recording preview URL so that we can build thumbnail URLs later
      const idxQ = recording.preview.indexOf('?');
      if (idxQ > -1) {
        this.previewQueryString = recording.preview.substring(idxQ + 1);
        const previewWithoutQuery = recording.preview.substring(0, idxQ);
        if (previewWithoutQuery.match(/\/[^\/]+$/)) {
          this.previewBaseURL = previewWithoutQuery.replace(/[^\/]+$/, '');
        }
      }

      this.initPreviewMosaic();
    }

    initSegments(segments) {
      this.segments = segments;

      this.startSegments = [];
      this.stopSegments = [];
      this.segmentTimes = [0];

      // Walk through each of the individual segment durations to build
      // accumlated totals for both starting and stopping at that index.
      let time = '0:00:00';
      let secs = 0.0;
      _.each(this.segments, (segment, i) => {
        if (segment.duration_ts === 0) {
          return;
        }

        const byteranges = segment.byte_ranges;
        if (
          byteranges !== undefined &&
          Object.keys(byteranges).length > 0 &&
          Object.keys(byteranges)[0].length > 0
        ) {
          _.each(byteranges[Object.keys(byteranges)[0]], (byterange) => {
            const duration = byterange.dur / 90000.0;
            this.startSegments.push({ segmentIndex: i, label: time, seconds: secs });
            secs += duration;
            this.segmentTimes.push(secs);
            time = secondsToFormattedLabel(secs);
            this.stopSegments.push({ segmentIndex: i, label: time, seconds: secs });
          });
        } else {
          const duration = segment.duration_ts / 90000.0;
          this.startSegments.push({ segmentIndex: i, label: time, seconds: secs });
          secs += duration;
          this.segmentTimes.push(secs);
          time = secondsToFormattedLabel(secs);
          this.stopSegments.push({ segmentIndex: i, label: time, seconds: secs });
        }
      });
    }

    setZoom(zoom) {
      const { $scope, $timeout } = this;

      // Force it between 100% and 1000%
      this.zoom = Math.max(1, Math.min(10, zoom));

      this.initPreviewMosaic();

      // Attempt to keep left handle in same position before/after resize
      let currentScroll = $('.zoom-scroll-pan-container').get(0).scrollLeft;
      let handles = $('.trim .rz-pointer');
      let currentLeftHandleOffset = handles.get(0).offsetLeft;
      let currentLeftHandlePosInViewport = currentLeftHandleOffset - currentScroll;

      $timeout(() => {
        // Need to kick the rzslider in the teeth a bit
        $scope.$broadcast('reCalcViewDimensions');
        $scope.$broadcast('rzSliderForceRender');

        // Wait for rzslider; then reposition window
        $timeout(() => {
          let newLeftHandleOffset = handles.get(0).offsetLeft;
          let newLeftHandlePosInViewport = newLeftHandleOffset - currentScroll;
          if (newLeftHandlePosInViewport != currentLeftHandlePosInViewport) {
            let nudge = currentLeftHandlePosInViewport - newLeftHandlePosInViewport;
            $('.zoom-scroll-pan-container').get(0).scrollLeft =
              $('.zoom-scroll-pan-container').get(0).scrollLeft - nudge;
            //console.log('nudged', nudge, 'to the left', $('.zoom-scroll-pan-container').get(0).scrollLeft);
          } else {
            //console.log('keeping zoom');
          }
          this.positionMasks();
        });
      });
    }

    zoomIn() {
      this.setZoom(this.zoom + 0.5);
    }

    zoomOut() {
      this.setZoom(this.zoom - 0.5);
    }

    panLeft() {
      var $container = $('.zoom-scroll-pan-container');
      var currentScroll = $container[0].scrollLeft;
      $container[0].scrollLeft = Math.max(0, currentScroll - 50);
    }

    panRight() {
      var $container = $('.zoom-scroll-pan-container');
      var $content = $('.zoom-scroll-pan-container > div');
      var currentScroll = $container[0].scrollLeft;
      var padding = 20;
      var maxScroll = $content[0].offsetWidth + padding - $container[0].offsetWidth;
      $container[0].scrollLeft = Math.min(maxScroll, currentScroll + 50);
    }

    initPreviewMosaic() {
      // Pick out N evenly spaced images out of the segments
      // Aim for background images approximately spaced every 75px
      let containerWidth = $('.mosaic').width();
      let numberImages = Math.ceil(containerWidth / 75);

      this.previewMosaic = [];
      if (this.segments.length <= numberImages) {
        // You can't always get what you want...
        numberImages = this.segments.length;
      }

      // Figure out start points we want
      // 0 sec, time / 8, 2 * time / 8, 3 * time / 8, ... 7 * time / 8
      // Figure out which segment image contains that time, and use it
      //let totalTime = _.last(this.stopSegments).seconds;
      //let cuePoints = _.range(8).map((i) => totalTime * i / 8.0);
      let totalSegments = this.segments.length;
      let cuePoints = _.range(numberImages).map((i) =>
        Math.floor((1.0 * totalSegments * i) / numberImages)
      );
      _.each(cuePoints, (frame) => this.previewMosaic.push(this.segmentPreviewURL(frame)));
      //console.log('Loaded mosaoic', this.segments, totalSegments, cuePoints, this.previewMosaic);
    }
  }
);



// WEBPACK FOOTER //
// ./app/directives/recording-trimmer.js